import { Button } from "@car/ui/components/button";
import { Skeleton } from "@car/ui/components/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@car/ui/components/toggle-group";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  ArrowLeft01Icon,
  BubbleChatIcon,
  CarAlertIcon,
  Message01Icon,
  PlusSignIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import { openLiveChat } from "@/lib/live-chat-open";
import {
  PageHeader,
  PanelEmpty,
  PanelQueryError,
  RatingStars,
  TicketStatusBadge,
} from "@/components/panel-bits";
import { messageCountText } from "@/components/ticket-bits";
import { telHref, useSiteContent } from "@/lib/content";
import { faDate, faNumber, faTime, toFaDigits } from "@/lib/format";
import { ticketTypeLabels, type SupportTicket } from "@/lib/panel";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/tickets/")({
  head: () => ({ meta: [{ title: "پشتیبانی و تیکت‌ها | پنل مشتریان تابان خودرو" }] }),
  validateSearch: z.object({ show: z.enum(["closed"]).optional().catch(undefined) }),
  component: TicketsPage,
});

const tehranDay = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tehran" });

/** «۱۴:۰۵» for today, the date otherwise */
const when = (iso: string) =>
  tehranDay.format(new Date(iso)) === tehranDay.format(new Date()) ? faTime(iso) : faDate(iso);

function TicketsPage() {
  const show = Route.useSearch().show ?? "active";
  const navigate = useNavigate({ from: "/panel/tickets/" });
  const query = useQuery(orpc.panel.tickets.list.queryOptions());
  const tickets = query.data?.tickets ?? [];
  const active = tickets.filter((ticket) => ticket.status !== "closed");
  const closed = tickets.filter((ticket) => ticket.status === "closed");
  const shown = show === "closed" ? closed : active;
  const awaiting = active.filter((ticket) => ticket.status === "answered").length;

  return (
    <div className="grid gap-6">
      <PageHeader
        title="پشتیبانی و تیکت‌ها"
        description="سؤال، درخواست یا شکایت را ثبت کنید و پاسخ کارشناسان را همین‌جا پیگیری کنید."
        action={
          <Button nativeButton={false} render={<Link to="/panel/tickets/new" />}>
            <HugeiconsIcon icon={PlusSignIcon} strokeWidth={1.8} />
            تیکت تازه
          </Button>
        }
      />

      <QuickHelp />

      {query.isPending ? (
        <div className="grid gap-2">
          {[0, 1, 2].map((index) => (
            <Skeleton key={index} className="h-20 rounded-2xl" />
          ))}
        </div>
      ) : query.isError ? (
        <PanelQueryError title="تیکت‌ها بارگیری نشد" query={query} />
      ) : tickets.length === 0 ? (
        <PanelEmpty
          icon={Message01Icon}
          title="هنوز تیکتی ثبت نکرده‌اید"
          description="هر سؤال یا درخواستی درباره خرید، سفارش، تعمیر یا پرداخت دارید، با یک تیکت بپرسید؛ پاسخ و پیگیری همین‌جا می‌ماند."
          action={
            <Button nativeButton={false} render={<Link to="/panel/tickets/new" />}>
              ثبت اولین تیکت
            </Button>
          }
        />
      ) : (
        <section className="grid gap-3" aria-labelledby="tickets-heading">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 id="tickets-heading" className="text-base font-black">
              {show === "closed" ? "تیکت‌های بسته" : "تیکت‌های جاری"}
              {awaiting > 0 && show !== "closed" ? (
                <span className="ms-2 text-sm font-bold text-emerald-700 dark:text-emerald-400">
                  {faNumber(awaiting)} پاسخ تازه
                </span>
              ) : null}
            </h2>
            <ToggleGroup
              value={[show]}
              onValueChange={(value) => {
                const next = value[0];
                if (next)
                  navigate({
                    search: { show: next === "closed" ? "closed" : undefined },
                    replace: true,
                  });
              }}
              variant="outline"
              size="sm"
              aria-label="نمایش تیکت‌ها"
            >
              <ToggleGroupItem value="active">جاری ({faNumber(active.length)})</ToggleGroupItem>
              <ToggleGroupItem value="closed">بسته ({faNumber(closed.length)})</ToggleGroupItem>
            </ToggleGroup>
          </div>
          {shown.length === 0 ? (
            <p className="rounded-2xl border border-dashed px-4 py-8 text-center text-sm text-muted-foreground">
              {show === "closed"
                ? "تیکت بسته‌ای ندارید."
                : "تیکت بازی ندارید؛ همه پیگیری‌ها تمام شده است."}
            </p>
          ) : (
            <ul className="overflow-hidden rounded-2xl border bg-card">
              {shown.map((ticket) => (
                <li key={ticket.id} className="border-b last:border-b-0">
                  <TicketRow ticket={ticket} />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

function TicketRow({ ticket }: { ticket: SupportTicket }) {
  const fresh = ticket.status === "answered";
  const last = ticket.last;
  return (
    <Link
      to="/panel/tickets/$ticketId"
      params={{ ticketId: ticket.id }}
      className="group/row flex items-start gap-3 px-4 py-4 transition-colors outline-none hover:bg-muted/50 focus-visible:bg-muted/60 sm:px-5"
    >
      <span
        className={cn(
          "mt-2 size-2.5 shrink-0 rounded-full",
          fresh
            ? "bg-emerald-500"
            : ticket.status === "closed"
              ? "bg-muted-foreground/30"
              : "bg-primary",
        )}
        aria-hidden
      />
      <span className="grid min-w-0 flex-1 gap-1">
        <span className="flex items-baseline gap-3">
          <span className={cn("min-w-0 flex-1 truncate", fresh ? "font-black" : "font-bold")}>
            {ticket.subject}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground tabular-nums">
            {when(last?.dateIso ?? ticket.createdIso)}
          </span>
        </span>
        {last ? (
          <span
            className={cn(
              "line-clamp-1 text-sm",
              fresh ? "text-foreground" : "text-muted-foreground",
            )}
            dir="auto"
          >
            <span className="font-bold">{last.from === "staff" ? "کارشناس: " : "شما: "}</span>
            {last.text}
            {last.files > 0 ? ` (${faNumber(last.files)} پیوست)` : ""}
          </span>
        ) : null}
        <span className="flex flex-wrap items-center gap-x-1 gap-y-1.5 text-xs text-muted-foreground">
          <TicketStatusBadge status={ticket.status} />
          <span className="ms-1">
            شماره {toFaDigits(String(ticket.number))}
            {SEP}
            {ticketTypeLabels[ticket.type]}
            {SEP}
            {messageCountText(ticket.messageCount)}
            {ticket.vehicleName ? `${SEP}${ticket.vehicleName}` : ""}
          </span>
          {ticket.rating !== null ? (
            <span className="flex items-center gap-1">
              <RatingStars value={ticket.rating} size="size-3.5" />
              {ticket.ratingComment ? (
                <span className="line-clamp-1" dir="auto">
                  «{ticket.ratingComment}»
                </span>
              ) : null}
            </span>
          ) : null}
        </span>
      </span>
      <HugeiconsIcon
        icon={ArrowLeft01Icon}
        strokeWidth={1.8}
        className="mt-1 size-4 shrink-0 text-muted-foreground transition-transform group-hover/row:-translate-x-0.5"
      />
    </Link>
  );
}

/** Faster ways than a ticket for what cannot wait */
function QuickHelp() {
  const { contact, liveChat } = useSiteContent();
  const chatOn = liveChat.enabled !== "off";
  if (!chatOn && !contact.hotlinePhone) return null;
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl bg-muted/60 px-4 py-3 text-sm">
      <span className="text-muted-foreground">پاسخ فوری‌تر لازم دارید؟</span>
      {chatOn ? (
        <button
          type="button"
          onClick={openLiveChat}
          className="inline-flex items-center gap-1.5 font-bold text-primary underline-offset-4 hover:underline"
        >
          <HugeiconsIcon icon={BubbleChatIcon} strokeWidth={1.8} className="size-4" />
          گفتگوی آنلاین با پشتیبانی
        </button>
      ) : null}
      {contact.hotlinePhone ? (
        <a
          href={telHref(contact.hotlinePhone)}
          className="inline-flex items-center gap-1.5 font-bold text-destructive underline-offset-4 hover:underline"
        >
          <HugeiconsIcon icon={CarAlertIcon} strokeWidth={1.8} className="size-4" />
          امداد جاده‌ای <span dir="ltr">{toFaDigits(contact.hotlinePhone)}</span>
        </a>
      ) : null}
    </div>
  );
}
